import type { Column, ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { Checkbox } from '@/components/ui/checkbox';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { LotSizingCurrentDataIndicator } from '@/features/lot-sizing/component/lot-sizing-current-data';
import { LotSizingQuantityCell } from '@/features/lot-sizing/component/lot-sizing-quantity-cell';
import { useLotSizingPurchaseOrderDraft } from '@/features/lot-sizing/lot-sizing-purchase-order-draft-context';
import { PimLink } from '@/features/shared/pim-link';
import { ProductHoverCard } from '@/features/shared/product-hover-card';
import { convertCubicCentimetersToCubicMeters } from '@/lib/convert';
import { formatNumberToVolume } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { LotSizingLine } from '@/types/core/lot-sizing/lot-sizing-line';

type LotSizingRefsTableMeta = {
  hasRelatedPurchaseOrders: boolean;
  onQuantityChange?: (rowId: string, quantity: number) => void;
  onCheckedChange?: (rowId: string, checked: boolean) => void;
  onBulkCheckedChange?: (checked: boolean) => void;
  isActionsDisabled?: boolean;
  isBulkCheckedLocked?: boolean;
};

export function useLotSizingTableColumn(): ColumnDef<LotSizingLine>[] {
  const { t } = useTranslation('lotSizing');
  const { quantityResetKey } = useLotSizingPurchaseOrderDraft();

  return useMemo(
    () => [
      {
        id: 'checked',
        accessorKey: 'checked',
        header: ({ table }) => {
          const meta = table.options.meta as LotSizingRefsTableMeta | undefined;
          const rows = table.getRowModel().rows;
          const allChecked =
            rows.length > 0 && rows.every((r) => Boolean(r.original.checked));
          const someChecked = rows.some((r) => Boolean(r.original.checked));
          return (
            <Checkbox
              aria-label={t('checkedAll')}
              checked={
                allChecked ? true : someChecked ? 'indeterminate' : false
              }
              disabled={
                !meta?.onBulkCheckedChange ||
                meta.isBulkCheckedLocked ||
                meta.isActionsDisabled
              }
              onCheckedChange={(value: boolean | 'indeterminate') => {
                const checked = !!value;
                meta?.onBulkCheckedChange?.(checked);
              }}
            />
          );
        },
        cell: ({ row, table }) => {
          const meta = table.options.meta as LotSizingRefsTableMeta | undefined;
          const checked = Boolean(row.original.checked);
          return (
            <Checkbox
              aria-label={t('checked')}
              checked={checked}
              disabled={!meta?.onCheckedChange || meta.isActionsDisabled}
              key={`checked-${row.id}-${checked}`}
              onCheckedChange={(value: boolean | 'indeterminate') => {
                meta?.onCheckedChange?.(row.id, !!value);
              }}
            />
          );
        },
        size: 24,
        enableSorting: false,
        meta: { label: t('checked') },
      },
      {
        id: 'productId',
        accessorFn: (row) => row.product.id,
        header: ({ column }: { column: Column<LotSizingLine, unknown> }) => (
          <DataTableColumnHeader column={column} label={t('product')} />
        ),
        cell: ({ row }) => <ProductHoverCard product={row.original.product} />,
        meta: { label: t('product') },
        enableColumnFilter: true,
      },
      {
        id: 'productName',
        accessorFn: (row) => row.product.title || null,
        header: ({ column }: { column: Column<LotSizingLine, unknown> }) => (
          <DataTableColumnHeader
            column={column}
            label={t('productNameLabel')}
          />
        ),
        cell: ({ cell }) => {
          const value = cell.renderValue<string>();
          return (
            <HoverCard>
              <HoverCardTrigger asChild>
                <span className='block max-w-[160px] cursor-pointer truncate text-xs hover:underline'>
                  {value}
                </span>
              </HoverCardTrigger>
              <HoverCardContent className='w-80'>
                <h4 className='font-semibold'>{value}</h4>
              </HoverCardContent>
            </HoverCard>
          );
        },
        enableColumnFilter: true,
        meta: { label: t('productNameLabel') },
      },
      {
        id: 'model',
        accessorFn: (row) => row.product.productSupplierId,
        header: ({ column }: { column: Column<LotSizingLine, unknown> }) => (
          <DataTableColumnHeader column={column} label={t('model')} />
        ),
        cell: ({ cell }) => {
          const value = cell.renderValue<string>();
          return (
            <HoverCard>
              <HoverCardTrigger asChild>
                <span className='block max-w-[160px] cursor-pointer truncate text-xs hover:underline'>
                  {String(value)}
                </span>
              </HoverCardTrigger>
              <HoverCardContent className='w-80'>
                <h4 className='font-semibold'>{String(value)}</h4>
              </HoverCardContent>
            </HoverCard>
          );
        },
        meta: { label: t('model') },
      },
      {
        id: 'coverage',
        accessorFn: (row) => row.purchasePlanning?.minCoverageQuantity,
        header: ({ column, table }) => (
          <div className='flex items-center gap-1'>
            <DataTableColumnHeader column={column} label={t('coverage')} />
            {(table.options.meta as LotSizingRefsTableMeta | undefined)
              ?.hasRelatedPurchaseOrders && <LotSizingCurrentDataIndicator />}
          </div>
        ),
        cell: ({ cell }) => <div>{cell.renderValue<number | string>()}</div>,
        meta: { label: t('coverage') },
      },
      {
        id: 'qtyCtn',
        accessorFn: (row) => Math.max(1, row.product.quantityPerPackage ?? 1),
        header: ({ column, table }) => (
          <div className='flex items-center gap-1'>
            <DataTableColumnHeader column={column} label={t('qtyCtn')} />
            {(table.options.meta as LotSizingRefsTableMeta | undefined)
              ?.hasRelatedPurchaseOrders && <LotSizingCurrentDataIndicator />}
          </div>
        ),
        cell: ({ cell, row }) => {
          const current = cell.getValue<number>();
          const snapshot = row.original.quantityPerPackage;
          return (
            <div>
              {current}
              {snapshot !== null && snapshot !== current && (
                <div className='whitespace-nowrap text-muted-foreground text-xs'>
                  {t('poQtyCtn', { qtyCtn: snapshot })}
                </div>
              )}
            </div>
          );
        },
        meta: { label: t('qtyCtn') },
      },
      {
        id: 'productVolume',
        accessorFn: (row) => row.productImportPackage?.volume ?? null,
        header: ({ column }: { column: Column<LotSizingLine, unknown> }) => (
          <DataTableColumnHeader column={column} label={t('volumePerRef')} />
        ),
        cell: ({ cell, row }) => {
          const value = cell.getValue<number | null>();
          if (value === null) {
            return (
              <PimLink
                className='text-blue-500 text-sm italic hover:underline'
                href={`/products/${row.original.product.id}/packaging`}
              >
                {t('provide')}
              </PimLink>
            );
          }
          return (
            <div>
              {formatNumberToVolume(
                convertCubicCentimetersToCubicMeters(value),
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                },
              )}
            </div>
          );
        },
        meta: { label: t('volumePerRef') },
      },
      {
        id: 'physicalStock',
        accessorFn: (row) => row.product.physicalStock,
        header: ({ column, table }) => (
          <div className='flex items-center gap-1'>
            <DataTableColumnHeader column={column} label={t('physicalStock')} />
            {(table.options.meta as LotSizingRefsTableMeta | undefined)
              ?.hasRelatedPurchaseOrders && <LotSizingCurrentDataIndicator />}
          </div>
        ),
        cell: ({ cell }) => {
          const value = cell.getValue<number | null>();
          if (value === null) {
            return <div>-</div>;
          }
          return (
            <div className={cn(value === 0 && 'font-medium text-destructive')}>
              {value}
            </div>
          );
        },
        meta: { label: t('physicalStock') },
      },
      {
        id: 'MOQ',
        accessorFn: (row) => row.product.moq,
        header: ({ column, table }) => (
          <div className='flex items-center gap-1'>
            <DataTableColumnHeader column={column} label={t('MOQ')} />
            {(table.options.meta as LotSizingRefsTableMeta | undefined)
              ?.hasRelatedPurchaseOrders && <LotSizingCurrentDataIndicator />}
          </div>
        ),
        cell: ({ cell, row }) => {
          const moq = cell.getValue<number>();
          return (
            <div
              className={cn(
                row.original.quantity < moq && 'font-medium text-amber-600',
              )}
            >
              {moq}
            </div>
          );
        },
        meta: { label: t('MOQ') },
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: ({ column }: { column: Column<LotSizingLine, unknown> }) => (
          <DataTableColumnHeader column={column} label={t('quantity')} />
        ),
        cell: ({ cell, row, table }) => {
          const quantity = cell.getValue<LotSizingLine['quantity']>();
          const meta = table.options.meta as LotSizingRefsTableMeta | undefined;

          return (
            <LotSizingQuantityCell
              key={`${row.id}-${quantityResetKey}`}
              disabled={!meta?.onQuantityChange || meta.isActionsDisabled}
              minimumOrderQuantity={row.original.product.moq}
              currentQuantityPerPackage={
                row.original.quantityPerPackage === null
                  ? row.original.product.quantityPerPackage
                  : undefined
              }
              quantityPerPackage={row.original.quantityPerPackage}
              onValueChange={meta?.onQuantityChange}
              rowId={row.id}
              value={quantity}
            />
          );
        },
        meta: { label: t('quantity') },
      },
    ],
    [quantityResetKey, t],
  );
}
